/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { Seo, Footer, KoinosWordmark, KedemWordmark } from '@components/systems'
import { MonolithBand, monoBandStyle } from '@components/systems/monolith/MonolithBands'
import { MonolithProductNav } from '@components/systems/monolith/MonolithProductNav'
import { mono, monoShared } from '@components/systems/monolith/monolith.tokens'
import { buildProductJsonLd, buildBreadcrumbJsonLd } from '@utils'
import { SEO_SITE, getSeoEntry } from '@src/configs/seo.config'
import { getProductDetail } from '@src/configs/productDetail.config'

import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '@redux/index'
import { SeasonProduct, SeasonProductType } from '@schemas'

interface PropsType {
	/** 대문자 제품 코드 — KDM6 · KDM4 · AMP2 · DS. 라우트가 넣는다 */
	code: string
}

/**
 * 제품 페이지 `/products/{code}` (2026-09-10, SITEMAP_PLAN §16).
 *
 * **이 제품이 무엇인가** 를 말하는 자리다. 랜딩 겸 정본 상세이고 색인 대상이다.
 * 가격·옵션·수량·장바구니는 여기 없다 — 그건 `/shop/{code}` 다. 이 페이지의 블랙 CTA 는
 * 맨 아래 Buy 하나뿐이고, 서브 네비의 Buy 는 밑줄 링크다(따라다니며 조르지 않는다, PERSONA §1).
 *
 * 형식은 `/products/pls8`(8 beats)을 따르고, 네 제품이 같은 유닛이라 화면은 이 한 장이다.
 * 내용은 `configs/productDetail.config.ts`.
 */
const MonolithProductDetailPage = (props: PropsType) => {
	// hook
	const navigate = useNavigate()

	const detail = getProductDetail(props.code)

	// redux state — 가격은 안 쓰고 JSON-LD 의 재고·가격 정보에만 넣는다
	const seasonProductList: SeasonProductType[] = useAppSelector(
		state => state.shop.productList,
	)

	const thisProduct: SeasonProductType = React.useMemo(() => {
		const found = seasonProductList.filter(
			aProduct => (aProduct.productCode ?? '').toUpperCase() === props.code.toUpperCase(),
		)
		return found.length === 0 ? new SeasonProduct() : found[0]
	}, [seasonProductList, props.code])

	const path = `/products/${props.code.toLowerCase()}`
	const buyPath = `/shop/${props.code.toLowerCase()}`

	const jsonLd = React.useMemo(() => {
		if (detail === undefined) return []

		return [
			buildProductJsonLd({
				name: detail.name,
				description: getSeoEntry(path).description,
				path: path,
				image: `${SEO_SITE.origin}${detail.hero.src}`,
				product: thisProduct,
				sku: detail.code,
			}),
			buildBreadcrumbJsonLd([
				{ name: 'Home', path: '/' },
				// 제품 페이지라 부모가 Products 다. 구매는 /shop/{code}.
				{ name: 'Products', path: '/products' },
				{ name: detail.name, path: path },
			]),
		]
	}, [detail, thisProduct, path])

	if (detail === undefined) {
		return null
	}

	const go = (to: string) => (e: React.MouseEvent) => {
		e.preventDefault()
		navigate(to)
	}

	/** 히어로 제목 — 규정이 있는 제품은 워드마크로만 (PRODUCT_NAMES §0) */
	const renderTitle = () => {
		if (detail.wordmark === 'koinos') {
			return <KoinosWordmark as="span" />
		}
		if (detail.wordmark === 'kedem') {
			return <KedemWordmark as="span" model={detail.model} />
		}
		return detail.name
	}

	return (
		<View css={style}>
			<Seo jsonLd={jsonLd} />

			<MonolithProductNav activeCode={props.code} />

			<View className="mnWrap">
				{/* 히어로 — 워드마크 · 태그라인 · 리드 · 대표컷. 설명은 여기서 끝낸다 */}
				<View className="mnPdHero">
					<h1 className="mnPdTitle">{renderTitle()}</h1>
					<p className="mnPdTagline">{detail.tagline}</p>
					<p className="mnPdLede">{detail.lede}</p>

					{/* 단종 판 — 리드 바로 아래, 히어로 안에서 가장 먼저 눈에 걸리는 자리 */}
					{detail.status !== undefined && (
						<View className="mnPdStatus">
							<span className="mnKicker mnPdStatusLabel">{detail.status.label}</span>
							{detail.status.lines.map(aLine => (
								<p key={aLine} className="mnPdStatusLine">
									{aLine}
								</p>
							))}
						</View>
					)}

					{/*
						상단 Buy. 클로즈에도 같은 버튼이 있다 — 같은 동작이라 "페이지당 주 CTA 하나"
						(DESIGN_SYSTEM §5)의 취지에 어긋나지 않는다. 긴 페이지라 위아래 두 곳에 둔다.
						단종 제품에는 두지 않는다 (2026-09-16) — 살 수 없는 것에 사라고 하지 않는다.
					*/}
					{detail.status === undefined && (
						<a className="mnCta mnPdHeroCta" href={buyPath} onClick={go(buyPath)}>
							Buy
						</a>
					)}
				</View>

				<View className="mnPdHeroShot">
					<img src={detail.hero.src} alt={detail.hero.alt} className="mnPdHeroImage" />
				</View>

				{detail.bands.map(aBand => (
					<MonolithBand key={aBand.kicker} band={aBand} />
				))}

				{/*
					클로즈 — 페이지의 유일한 블랙 CTA.
					단종 제품은 그 자리를 밑줄 링크 둘이 받는다: 쓰던 사람은 매뉴얼로, 지금 살 것을
					찾는 사람은 현행 제품으로 간다.
				*/}
				<View className="mnBand mnDetailBand mnPdClose">
					<h2 className="mnPdCloseHeading">{detail.closeHeading}</h2>
					{detail.status === undefined ? (
						<a className="mnCta mnPdCloseCta" href={buyPath} onClick={go(buyPath)}>
							Buy
						</a>
					) : (
						<View className="mnPdCloseLinks">
							<a className="mnTextLink" href="/manuals" onClick={go('/manuals')}>
								Manuals
							</a>
							<a
								className="mnTextLink"
								href="/products/pls8"
								onClick={go('/products/pls8')}
							>
								Koinos PLS 8
							</a>
						</View>
					)}
				</View>
			</View>

			<Footer />
		</View>
	)
}

export default MonolithProductDetailPage

const style = css({
	...monoShared,
	...monoBandStyle,
	position: 'relative',
	width: '100%',

	/**
	 * 히어로 — 제목 · 태그라인 · 리드가 왼쪽에 서고, 대표컷이 그 아래 전폭으로 눕는다.
	 * 구매 화면(`/shop/{code}`)은 사진과 패널을 나란히 두지만, 여기는 팔지 않으므로
	 * 사진이 폭을 다 쓴다.
	 */
	'.mnPdHero': {
		padding: 'clamp(56px, 7vw, 100px) 0 clamp(36px, 4vw, 56px)',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',

		'.mnPdTitle': {
			fontSize: 'clamp(44px, 6.6vw, 96px)',
			lineHeight: 1,
			fontWeight: 300,
			letterSpacing: '-0.015em',
			color: mono.ink,
		},

		'.mnPdTagline': {
			marginTop: 'clamp(20px, 2.2vw, 32px)',
			fontSize: 'clamp(24px, 2.6vw, 36px)',
			lineHeight: 1.3,
			fontWeight: 200,
			color: mono.ink,
		},

		'.mnPdLede': {
			marginTop: 'clamp(20px, 2vw, 28px)',
			fontSize: 17,
			lineHeight: 1.7,
			fontWeight: 300,
			color: mono.dim,
			maxWidth: 620,
		},

		// 단종 판 — 면으로 끊어 히어로 본문과 구분한다. 액센트는 쓰지 않는다 (PERSONA §2)
		'.mnPdStatus': {
			marginTop: 'clamp(24px, 2.6vw, 36px)',
			padding: 'clamp(20px, 2.2vw, 28px)',
			background: mono.surface,
			border: `1px solid ${mono.line}`,
			borderRadius: 2,
			maxWidth: 620,
			display: 'flex',
			flexDirection: 'column',
			gap: 10,

			'.mnPdStatusLabel': {
				color: mono.ink,
			},
			'.mnPdStatusLine': {
				fontSize: 16,
				lineHeight: 1.7,
				fontWeight: 300,
				color: mono.ink,

				'&:last-of-type': {
					color: mono.dim,
				},
			},
		},

		'.mnPdHeroCta': {
			marginTop: 'clamp(28px, 3vw, 44px)',
			textDecoration: 'none',
		},
	},

	'.mnPdHeroShot': {
		width: '100%',
		paddingBottom: 'clamp(24px, 3vw, 44px)',

		'.mnPdHeroImage': {
			display: 'block',
			width: '100%',
			border: `1px solid ${mono.line}`,
			background: mono.surface,
		},
	},

	'.mnPdClose': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		textAlign: 'center',
		padding: 'clamp(72px, 9vw, 130px) 0',

		'.mnPdCloseHeading': {
			fontSize: 'clamp(28px, 3.2vw, 46px)',
			lineHeight: 1.2,
			fontWeight: 200,
			color: mono.ink,
			maxWidth: 900,
		},

		'.mnPdCloseCta': {
			marginTop: 'clamp(32px, 3.6vw, 48px)',
			textDecoration: 'none',
		},

		// 단종 제품의 클로즈 — 블랙 CTA 자리를 대신하는 링크 둘
		'.mnPdCloseLinks': {
			marginTop: 'clamp(32px, 3.6vw, 48px)',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			gap: 'clamp(24px, 3vw, 40px)',
			flexWrap: 'wrap',
		},
	},
})
